import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PlayerBannerPlayerIcon from "../../assets/icons/PlayerBannerPlayerIcon";
import Height from "../../assets/icons/Height";
import Weight from "../../assets/icons/Weight";
import Birthday from "../../assets/icons/Birthday";
import Foot from "../../assets/icons/Foot";
import Contract from "../../assets/icons/Contract";
import Club from "../../assets/icons/Club";
import Agent from "../../assets/icons/Agent";
import MarketValue from "../../assets/icons/MarketValue";
import League from "../../assets/icons/League";
import StatsBox from "./StatsBox";
import {
  formatCurrencyWithSymbol,
  formatGroupedNumber,
} from "../../utils/currencyFormatter";
import { resolveNationalities } from "../../utils/nationalityFlags";
import AvailabilityPill from "./AvailabilityPill";

// Country name to ISO code mapping (common countries in football)
const COUNTRY_TO_ISO = {
  // Europe
  england: "gb-eng",
  scotland: "gb-sct",
  wales: "gb-wls",
  "northern ireland": "gb-nir",
  spain: "es",
  france: "fr",
  germany: "de",
  italy: "it",
  portugal: "pt",
  netherlands: "nl",
  belgium: "be",
  croatia: "hr",
  denmark: "dk",
  sweden: "se",
  norway: "no",
  poland: "pl",
  austria: "at",
  switzerland: "ch",
  "czech republic": "cz",
  turkey: "tr",
  greece: "gr",
  serbia: "rs",
  ukraine: "ua",
  russia: "ru",
  ireland: "ie",
  romania: "ro",
  slovakia: "sk",
  hungary: "hu",
  bulgaria: "bg",
  slovenia: "si",
  finland: "fi",
  iceland: "is",
  albania: "al",
  "bosnia and herzegovina": "ba",
  "north macedonia": "mk",
  montenegro: "me",
  kosovo: "xk",

  // South America
  brazil: "br",
  argentina: "ar",
  uruguay: "uy",
  colombia: "co",
  chile: "cl",
  paraguay: "py",
  peru: "pe",
  ecuador: "ec",
  venezuela: "ve",
  bolivia: "bo",

  // North/Central America
  mexico: "mx",
  usa: "us",
  "united states": "us",
  canada: "ca",
  "costa rica": "cr",
  panama: "pa",
  jamaica: "jm",

  // Africa
  egypt: "eg",
  nigeria: "ng",
  senegal: "sn",
  morocco: "ma",
  algeria: "dz",
  tunisia: "tn",
  ghana: "gh",
  cameroon: "cm",
  "ivory coast": "ci",
  "south africa": "za",
  mali: "ml",
  "burkina faso": "bf",

  // Asia
  japan: "jp",
  "south korea": "kr",
  iran: "ir",
  "saudi arabia": "sa",
  australia: "au",
  qatar: "qa",
  uae: "ae",
  "united arab emirates": "ae",
  china: "cn",
  india: "in",
  iraq: "iq",
  uzbekistan: "uz",
};

// Helper to get country flag URL from nationality object or name
const getCountryFlagUrl = (nationality) => {
  if (!nationality) return null;

  // If nationality is an object with image_url, use it directly
  if (typeof nationality === "object" && nationality.image_url) {
    return nationality.image_url;
  }

  if (typeof nationality === "object" && !nationality.image_url) {
    return null;
  }

  // Otherwise, treat as string and use flagcdn
  const normalizedName = nationality.toLowerCase().trim();
  const isoCode = COUNTRY_TO_ISO[normalizedName];

  if (isoCode) {
    return `https://flagcdn.com/w80/${isoCode}.png`;
  }

  // Fallback: try to use first 2 letters as ISO code (may not always work)
  const fallbackCode = normalizedName.substring(0, 2);
  return `https://flagcdn.com/w80/${fallbackCode}.png`;
};

// Every citizenship the header should show, in the platform's priority order:
// national team first, then the remaining passports (see
// utils/nationalityFlags.resolveNationalities and the backend's
// dashboard/utils/nationality_resolver.py). The header is the widest surface a
// player has, so it shows ALL of them rather than the prioritised one alone —
// Jude Bellingham reads England + Ireland here, not Ireland by itself.
const resolveHeroFlags = (player) => {
  const source = player?.player || player || {};
  const list = resolveNationalities({
    nationalities: source.nationalities || player?.nationalities,
    nationality: source.nationality || player?.nationality,
    birth_country: source.birth_country || player?.birth_country,
  });
  return list.map(resolveFlagInfo).filter((f) => f && f.url);
};

// Tooltip text for the header flags: the countries in priority order. Country
// names come from the API and are not translated, so this adds no i18n row.
const heroFlagTitle = (player) =>
  resolveHeroFlags(player)
    .map((f) => f.name)
    .filter(Boolean)
    .join(" \u00b7 ");

// Resolve flag info (url + name) from various object/string shapes
const resolveFlagInfo = (value) => {
  if (!value) return null;
  if (typeof value === "object") {
    const name = value.name || value.country || value.country_name || "";
    const url = value.image_url || getCountryFlagUrl(name);
    if (!name && !url) return null;
    return { url, name };
  }
  if (typeof value === "string") {
    return { url: getCountryFlagUrl(value), name: value };
  }
  return null;
};

// Position code to label mapping
const POSITION_CODE_TO_LABEL = {
  gk: "Goalkeeper",
  rb: "Right Back",
  rcb: "Right Centre Back",
  lcb: "Left Centre Back",
  lb: "Left Back",
  rw: "Right Winger",
  rcmf: "Right Centre Midfielder",
  lcmf: "Left Centre Midfielder",
  lw: "Left Winger",
  ss: "Second Striker",
  cf: "Striker",
  amf: "Attacking Midfielder",
  rcmf3: "Right Centre Midfielder (3 at the back)",
  dmf: "Defensive Midfielder",
  lcmf3: "Left Centre Midfielder (3 at the back)",
  rdmf: "Right Defensive Midfielder",
  ldmf: "Left Defensive Midfielder",
  ramf: "Right Attacking Midfielder",
  lamf: "Left Attacking Midfielder",
  rwf: "Right Wing Forward",
  lwf: "Left Wing Forward",
  rcb3: "Right Centre Back (3 at the back)",
  cb: "Centre Back",
  lcb3: "Left Centre Back (3 at the back)",
  rwb: "Right Wingback",
  lwb: "Left Wingback",
  rb5: "Right Back (5 at the back)",
  lb5: "Left Back (5 at the back)",
  cm: "Centre Midfielder",
};

// Helper to get position label from code
const getPositionLabel = (code) => {
  if (!code) return "-";
  const normalizedCode = code.toString().trim().toLowerCase();
  return POSITION_CODE_TO_LABEL[normalizedCode] || code.toUpperCase();
};

// Helper to calculate age
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "-";
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper to format market value. Delegates to the shared formatter, so the
// banner groups with the platform's apostrophe and spells CHF the way every
// other surface does (Marin, 2026-08-02).
const formatMarketValue = (value, currency, numeral) => {
  if (!value) return "-";
  const num = parseFloat(value);
  const multiplier = numeral === "m" ? 1000000 : numeral === "k" ? 1000 : 1;
  const fullValue = num * multiplier;

  return formatCurrencyWithSymbol(fullValue, currency || "EUR");
};

// Helper to format date like "Feb 05, 1985"
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const PlayerHeroBanner = ({
  player,
  className = "",
  showMarketValue = true,
  showAgent = true,
  isOnClickNavigate = false,
  showSimplePositionIfNoDetailed = false,
  // Paket Injuries (16.09.2026): current availability ({status, reason, since, expected_return, missed_matches,
  // as_of}) — one pill next to the GBE pill; fit/unknown/null renders nothing.
  availability = null,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Calculate age and format market value - handle both old and new API response formats
  const playerAge = calculateAge(
    player?.player?.date_of_birth || player?.date_of_birth
  );
  const marketValueFormatted = formatMarketValue(
    player?.market_value?.sportdevs_value
  );
  const formattedDOB = formatDate(
    player?.player?.date_of_birth || player?.date_of_birth
  );

  // Beweis-Freistatus (PR #431 payload, additiv): a proven free agent shows
  // "Free Agent" in the club slot + a clickable "Last Club" chip instead of
  // any (possibly stale) current-club fallback below — including the
  // statistics[0].team shape, which would otherwise resurface an old club.
  const isFreeAgent = player?.is_free_agent === true;
  const lastClub = player?.last_club || null;
  const freeSince = player?.free_since || null;

  const handleLastClubClick = (e) => {
    e.stopPropagation();
    if (lastClub?.id) {
      navigate(`/search/club/${lastClub.id}`);
    }
  };


  // Free-Agent slot (Marin 2026-08-20): badge + clickable "Last Club" chip.
  const freeAgentBlock = isFreeAgent ? (
    <>
      <span
        className="bg-emerald-500/90 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0"
        title={freeSince ? `Free agent since ${freeSince}` : undefined}
      >
        Free Agent{freeSince ? ` · since ${freeSince}` : ""}
      </span>
      {lastClub?.name && (
        <span
          className={`flex items-center gap-1.5 text-xs text-white/80 flex-shrink-0 ${lastClub.id ? "cursor-pointer hover:text-white transition-colors" : ""}`}
          onClick={lastClub.id ? handleLastClubClick : undefined}
          role={lastClub.id ? "button" : undefined}
          tabIndex={lastClub.id ? 0 : undefined}
          onKeyDown={lastClub.id ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleLastClubClick(e);
            }
          } : undefined}
        >
          <span className="text-white/60">Last Club :</span>
          {lastClub.image_url ? (
            <img
              src={lastClub.image_url}
              alt={lastClub.name}
              className="h-5 w-5 object-contain"
            />
          ) : null}
          <span className="font-medium">{lastClub.name}</span>
        </span>
      )}
    </>
  ) : null;

  // Get current club info - handle both old and new API response formats
  const getCurrentClubInfo = () => {
    if (isFreeAgent) {
      // Proven free agent: never resolve a "current" club from any legacy
      // shape — the slot renders the Free Agent badge instead.
      return { id: null, name: null, image: null, country: null };
    }
    // Try multiple shapes to resolve the club id
    const candidateIds = [
      player?.team?.id,
      player?.team_id,
      player?.player?.team?.id,
      player?.player?.team_id,
      player?.current_club?.id,
      player?.club_id,
      player?.statistics?.[0]?.team?.id,
    ];

    const resolvedId = candidateIds.find((v) => v !== undefined && v !== null);

    return {
      id: resolvedId || null,
      name:
        player?.team?.name ||
        player?.club_name ||
        player?.statistics?.[0]?.team?.name ||
        "N/A",
      image: player?.team?.image_url || player?.club_image,
      country: player?.team?.country || null,
    };
  };

  const currentClub = getCurrentClubInfo();

  // Handle club logo click - navigate to club page
  const handleClubLogoClick = (e) => {
    e.stopPropagation(); // Prevent triggering parent onClick
    if (currentClub.id) {
      navigate(`/search/club/${currentClub.id}`);
    }
  };

  // Determine whether to show detailed positions or a single position line
  const detailedPositionsObj =
    player?.player?.tp_detailed_position ||
    player?.tp_detailed_position ||
    player?.tpDetailedPosition ||
    player?.tpDetailedPositions ||
    null;

  const hasAnyDetailed =
    detailedPositionsObj &&
    typeof detailedPositionsObj === "object" &&
    (detailedPositionsObj.primary_position ||
      detailedPositionsObj.secondary_position ||
      detailedPositionsObj.teritory_position ||
      detailedPositionsObj.tertiary_position ||
      detailedPositionsObj.primaryPosition ||
      detailedPositionsObj.secondaryPosition ||
      detailedPositionsObj.tertiaryPosition);

  const showSimplePosition = showSimplePositionIfNoDetailed && !hasAnyDetailed;

  const simplePosition =
    player?.player?.position ||
    player?.position ||
    player?.player?.main_position ||
    player?.main_position ||
    player?.player?.role?.name ||
    player?.role?.name ||
    player?.player?.position_name ||
    player?.position_name ||
    player?.player?.position_code ||
    player?.position_code ||
    "-";

  const mappingOfDivison = (divNumber) => {
    if (divNumber === 1) return "1st Tier";
    else if (divNumber === 2) return "2nd Tier";
    else if (divNumber === 3) return "3rd Tier";
    else return `${divNumber}th Tier`;
  };

  return (
    <div
      onClick={() => {
        if (isOnClickNavigate) {
          const navigateId =
            player?.player?.wyscout_id ||
            player?.player?.id ||
            player?.wyscout_id ||
            player?.id;
          if (navigateId) {
            navigate(`/search/player/${navigateId}`);
          }
        }
      }}
      className={`bg-gradient-to-r from-[#3043D1] to-[#1A212F] border border-[#D6DBEA] ${isMobile ? 'flex flex-col' : 'flex flex-col md:flex-row'} items-center rounded-xl text-white relative overflow-hidden p-4 md:p-6 cursor-pointer gap-6 ${className}`}
    >
      {/* Logo, Name, Flag, and Club Logo - One Line (Mobile) / Separate (Desktop) */}
      <div className={`${isMobile ? 'flex items-center flex-wrap gap-3 w-full' : 'hidden md:block md:w-[94px] md:h-[94px] flex-shrink-0 bg-white rounded-lg p-1'}`}>
        {isMobile ? (
          <>
            {/* Player Image - Mobile */}
            <div className="w-16 h-16 flex-shrink-0 bg-white rounded-lg p-1">
              {player?.player?.image_url ||
              player?.image_path ||
              player?.profile_image ? (
                <img
                  src={
                    player?.player?.image_url ||
                    player.image_path ||
                    player.profile_image
                  }
                  alt={
                    player?.player?.name ||
                    player.display_name ||
                    player.name ||
                    player.full_name
                  }
                  className="w-full h-full object-cover rounded-md"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center">
                  <span className="text-gray-400 text-2xl font-bold">
                    {(
                      player?.player?.name ||
                      player?.display_name ||
                      player?.name ||
                      player?.full_name
                    )?.charAt(0) || "P"}
                  </span>
                </div>
              )}
            </div>

            {/* Player Name */}
            <h1 className="text-xl font-bold leading-tight text-white" data-no-translate="true">
              {player?.player?.firstname} {player?.player?.lastname}
            </h1>

            {/* Nationality Flags — all citizenships, national team first.
                They sit in the wrapping name row, so a second or third flag
                pushes onto the next line at 375px instead of squeezing. */}
            {resolveHeroFlags(player).map((flag, i) => (
              <img
                key={flag.url || flag.name || i}
                src={flag.url}
                alt={flag.name}
                title={heroFlagTitle(player)}
                className="h-5 w-8 object-cover rounded shadow-sm flex-shrink-0"
              />
            ))}

            {/* Club Logo */}
            {currentClub.image ? (
              <img
                src={currentClub.image}
                alt={currentClub.name || ""}
                className={`h-6 w-6 object-contain flex-shrink-0 ${currentClub.id ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                onClick={currentClub.id ? handleClubLogoClick : undefined}
                role={currentClub.id ? "button" : undefined}
                tabIndex={currentClub.id ? 0 : undefined}
                onKeyDown={currentClub.id ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClubLogoClick(e);
                  }
                } : undefined}
              />
            ) : (
              currentClub.name && (
                <div
                  className={`h-6 w-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 ${currentClub.id ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  onClick={currentClub.id ? handleClubLogoClick : undefined}
                  role={currentClub.id ? "button" : undefined}
                  tabIndex={currentClub.id ? 0 : undefined}
                  onKeyDown={currentClub.id ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClubLogoClick(e);
                    }
                  } : undefined}
                >
                  <span className="text-white text-[10px] font-bold">
                    {currentClub.name.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )
            )}
            {freeAgentBlock}
          </>
        ) : (
          /* Desktop Logo */
          <>
            {player?.player?.image_url ||
            player?.image_path ||
            player?.profile_image ? (
              <img
                src={
                  player?.player?.image_url ||
                  player.image_path ||
                  player.profile_image
                }
                alt={
                  player?.player?.name ||
                  player.display_name ||
                  player.name ||
                  player.full_name
                }
                className="w-full h-full object-cover rounded-md"
              />
            ) : (
              <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center">
                <span className="text-gray-400 text-2xl font-bold">
                  {(
                    player?.player?.name ||
                    player?.display_name ||
                    player?.name ||
                    player?.full_name
                  )?.charAt(0) || "P"}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Middle Section - Player Info */}
      <div className={`flex-1 flex flex-col gap-3 min-w-0 ${isMobile ? 'w-full' : ''}`}>
        {/* Name Header - Desktop Only */}
        <div className={`hidden md:flex items-center gap-3`}>
          <h1 className="text-2xl md:text-[32px] font-bold leading-tight text-white whitespace-nowrap overflow-hidden text-overflow-ellipsis" data-no-translate="true">
            {player?.player?.firstname} {player?.player?.lastname}
          </h1>

          {/* Nationality Flags — all citizenships, national team first. */}
          {resolveHeroFlags(player).map((flag, i) => (
            <img
              key={flag.url || flag.name || i}
              src={flag.url}
              alt={flag.name}
              title={heroFlagTitle(player)}
              className="h-5 w-8 object-cover rounded shadow-sm"
            />
          ))}

          {/* Club Logo (Small) */}
          {currentClub.image ? (
            <img
              src={currentClub.image}
              alt={currentClub.name || ""}
              className={`h-6 w-6 object-contain ${currentClub.id ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
              onClick={currentClub.id ? handleClubLogoClick : undefined}
              role={currentClub.id ? "button" : undefined}
              tabIndex={currentClub.id ? 0 : undefined}
              onKeyDown={currentClub.id ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClubLogoClick(e);
                }
              } : undefined}
            />
          ) : (
            currentClub.name && (
              <div
                className={`h-6 w-6 bg-white/20 rounded-full flex items-center justify-center ${currentClub.id ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                onClick={currentClub.id ? handleClubLogoClick : undefined}
                role={currentClub.id ? "button" : undefined}
                tabIndex={currentClub.id ? 0 : undefined}
                onKeyDown={currentClub.id ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClubLogoClick(e);
                  }
                } : undefined}
              >
                <span className="text-white text-[10px] font-bold">
                  {currentClub.name.substring(0, 2).toUpperCase()}
                </span>
              </div>
            )
          )}
          {freeAgentBlock}
        </div>

        {/* Positions Row */}
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-wrap'} ${isMobile ? 'gap-y-2' : 'gap-x-3 gap-y-2'} ${isMobile ? 'text-xs' : 'text-sm'} ${isMobile ? '' : 'items-center'} ${isMobile ? 'w-full' : ''}`}>
          {!isMobile && <PlayerBannerPlayerIcon />}

          {showSimplePosition ? (
            <div className={`flex ${isMobile ? 'flex-row items-center' : 'items-center'} gap-2`}>
              {isMobile && <PlayerBannerPlayerIcon />}
              <div>
                <div className="text-white/60 mb-0.5">Primary Position :</div>
                <div className="font-medium text-white">{simplePosition}</div>
              </div>
            </div>
          ) : (
            <>
              <div className={`flex ${isMobile ? 'flex-row items-center' : 'items-center'} gap-2`}>
                {isMobile && <PlayerBannerPlayerIcon />}
                <div>
                  <div className="text-white/60 mb-0.5">Primary Position :</div>
                  <div className="font-medium text-white">
                    {player?.player?.tp_detailed_position?.primary_position ||
                      "-"}
                  </div>
                </div>
              </div>
              {!isMobile && <div className="border-l border-white/10"></div>}
              <div className={isMobile ? 'ml-[20px]' : ''}>
                <div className="text-white/60 mb-0.5">Secondary Position :</div>
                <div className="font-medium text-white">
                  {player?.player?.tp_detailed_position?.secondary_position ||
                    "-----"}
                </div>
              </div>
              {!isMobile && <div className="border-l border-white/10"></div>}
              <div className={isMobile ? 'ml-[20px]' : ''}>
                <div className="text-white/60 mb-0.5">Tertiary Position :</div>
                <div className="font-medium text-white">
                  {player?.player?.tp_detailed_position?.teritory_position ||
                    player?.player?.tp_detailed_position?.tertiary_position ||
                    "-----"}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Physical Stats Row. Mobile: pl (not ml) keeps the 28px indent that
            aligns with the position labels WITHOUT widening the row past its
            container — a left margin on a w-full row pushed the last 28px
            under the banner's overflow-hidden edge, clipping the Foot entry.
            With padding, flex-wrap measures the visible width, so a tight
            entry wraps onto its own line instead of being cut off. */}
        <div className={`flex items-center flex-wrap ${isMobile ? 'gap-3' : 'gap-x-6 gap-y-2'} text-sm mt-1 ${isMobile ? 'w-full pl-[28px]' : ''}`}>
          {/* Height */}
          <div className="flex items-center gap-1">
            <Height />
            <span>{player?.player?.height_cm || player?.height || "0"} cm</span>
          </div>

          {/* Weight */}
          <div className="flex items-center gap-1">
            <Weight />
            <span>{player?.player?.weight_kg || player?.weight || "0"} Kg</span>
          </div>

          {/* DOB */}
          <div className="flex items-center gap-1">
            <Birthday />
            <span>
              {formattedDOB}{" "}
              <span className="font-bold">
                ({playerAge !== "-" ? playerAge : "0"})
              </span>
            </span>
          </div>

          {/* Preferred Foot */}
          {(() => {
            const foot = player?.player?.foot || player?.foot;
            if (!foot) return null;
            const footStr = String(foot);
            const footLabel =
              footStr.charAt(0).toUpperCase() + footStr.slice(1).toLowerCase();
            return (
              <div className="flex items-center gap-1">
                <Foot />
                <span>{footLabel}</span>
              </div>
            );
          })()}

          {/* Contract until — the public profile API delivers
              player.contract_expiration (TheSports-first, Wyscout fallback);
              formatted like the DOB next to it. Hidden when the API has no
              date, same as the Foot entry. The row wraps (flex-wrap), so on
              375px this entry drops to its own line instead of clipping.
              Kept as ONE text node so the existing static translation
              pattern "Contract until {year}" (agent-tender-v2-responses)
              matches it — no new bundle rows needed. */}
          {(() => {
            const contractRaw =
              player?.player?.contract_expiration || player?.contract_expiration;
            if (!contractRaw) return null;
            const contractFormatted = formatDate(contractRaw);
            if (contractFormatted === "-") return null;
            return (
              <div className="flex items-center gap-1">
                <Contract />
                <span>{`Contract until ${contractFormatted}`}</span>
              </div>
            );
          })()}

          <AvailabilityPill availability={availability} />
        </div>
      </div>

      {/* Right Section - Stats Boxes */}
      <div className={`flex flex-col gap-3 ${isMobile ? 'w-full' : 'min-w-[300px]'}`}>
        {/* Top Row */}
        <div className={`flex items-center ${isMobile ? 'flex-col' : ''} gap-3`}>
          {/* Club Box */}
          <StatsBox
            icon={<Club />}
            label="Club :"
            className={isMobile ? 'w-full' : 'flex-1 min-w-[140px]'}
          >
            <span
              className="text-sm font-bold leading-tight line-clamp-1"
              title={currentClub?.name || "-"}
            >
              {currentClub?.name || "-"}
            </span>
          </StatsBox>

          {/* Agent Box */}
          {showAgent && (
            <StatsBox
              icon={<Agent />}
              label="Agent :"
              className={isMobile ? 'w-full' : 'flex-1 min-w-[140px]'}
            >
              <span
                className="text-sm font-bold leading-tight line-clamp-1"
                title={player?.agent?.name || "-"}
              >
                {player?.agent?.name || "-"}
              </span>
            </StatsBox>
          )}

          {/* Market Value Box */}
          {showMarketValue && (
            <StatsBox
              icon={<MarketValue />}
              label="Market Value :"
              className={isMobile ? 'w-full' : 'flex-1 min-w-[140px]'}
            >
              {player?.market_value?.value ? (
                <span className="text-sm font-bold leading-tight">
                  {/* Same grouping helper as formatMarketValue above — this
                      second, inline render of the market value used to
                      hardcode an en-US comma while the helper 460 lines up
                      did too, so the file was at least internally consistent;
                      it has to move with it now that the separator is the
                      platform's apostrophe (Marin, 2026-08-02). */}
                  {player?.market_value?.currency || "€"}
                  {formatGroupedNumber(player.market_value.value)}
                </span>
              ) : (
                <span className="text-sm font-bold leading-tight">
                  {marketValueFormatted === "-" ? "-" : marketValueFormatted}
                </span>
              )}
            </StatsBox>
          )}
        </div>

        {/* Bottom Row - League/Tier */}
        <div className={`flex ${isMobile ? 'justify-start' : 'justify-end'}`}>
          <StatsBox
            icon={<League />}
            label={
              player?.player?.current_competition?.division_level
                ? mappingOfDivison(
                    player.player.current_competition.division_level
                  )
                : "Tier -"
            }
            className={isMobile ? 'w-full' : 'min-w-[200px]'}
          >
            <span className="text-sm font-bold leading-tight">
              {player?.player?.current_competition?.name ||
                player?.player?.current_competition?.country?.name ||
                "-"}
            </span>
          </StatsBox>
        </div>
      </div>
    </div>
  );
};

export default PlayerHeroBanner;
